/**
 * get_test_artifacts Tool - Retrieve detailed test artifacts for a completed job
 *
 * Returns rich test data: transcript, console logs, network requests, events,
 * conversation history, key moments. Use after get_job to investigate test
 * failures or understand test results in detail.
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import {
  JOB_ARTIFACT_TYPES,
  describeEvent,
  describePageState,
  type JobArtifactType,
  type JobArtifactResponses,
} from '@runhuman/shared';
import type { JobClient } from '../clients/job-client.js';

export interface GetTestArtifactsArgs {
  jobId: string;
  artifacts?: string[];
}

export const getTestArtifactsToolDefinition: Tool = {
  name: 'get_test_artifacts',
  description: `Get detailed test artifacts for a completed QA test job.

Returns rich test data that helps investigate test results in detail:
- transcript: Voice transcript with timestamps from the tester's session
- console-logs: Browser console output (errors, warnings, logs)
- network-requests: HTTP requests made during testing
- events: User interaction events (clicks with element, typed values, form submits, navigation with full URL) plus any page errors
- page-states: Page snapshots (full URL incl. query string + accessibility-tree summary) captured at navigations/clicks/errors — the highest-fidelity "what page was the tester on" record
- conversation: Chat messages between tester and AI assistant
- key-moments: AI-identified significant moments during testing
- recording: Screen-recording download URL(s) once the video is composed

Use this after get_job when you need to dig into WHY a test passed or failed.
Requires a paid plan for all artifact types except structured-output and recording.`,
  inputSchema: {
    type: 'object',
    properties: {
      jobId: {
        type: 'string',
        description: 'Job ID to get artifacts for',
      },
      artifacts: {
        type: 'array',
        items: {
          type: 'string',
          enum: [...JOB_ARTIFACT_TYPES],
        },
        description: 'Which artifacts to retrieve. Defaults to all available types.',
      },
    },
    required: ['jobId'],
  },
};

function formatArtifact(type: JobArtifactType, data: Record<string, unknown>): string {
  const header = `=== ${type.replace(/-/g, ' ').toUpperCase()} ===\n`;

  switch (type) {
    case 'structured-output': {
      const result = data.result as Record<string, unknown> | undefined;
      if (!result) return header + 'No structured output available\n';
      return header + JSON.stringify(result, null, 2) + '\n';
    }

    case 'transcript': {
      const transcription = data.transcription as { segments?: Array<{ text: string; words: Array<{ start: number }> }> } | undefined;
      if (!transcription?.segments?.length) return header + 'No transcript available\n';
      return header + transcription.segments.map(seg => {
        const time = seg.words[0] ? formatSeconds(seg.words[0].start) : '00:00';
        return `[${time}] ${seg.text}`;
      }).join('\n') + '\n';
    }

    case 'console-logs': {
      const logs = data.consoleMessages as Array<{ type: string; timestamp: string; message: string }> | undefined;
      if (!logs?.length) return header + 'No console logs recorded\n';
      return header + logs.map(log => {
        const time = new Date(log.timestamp).toLocaleTimeString();
        return `[${log.type}] ${time} ${log.message}`;
      }).join('\n') + '\n';
    }

    case 'network-requests': {
      const requests = data.networkRequests as Array<{ method: string; url: string; status?: number; timestamp: string }> | undefined;
      if (!requests?.length) return header + 'No network requests recorded\n';
      return header + requests.map(req => {
        const time = new Date(req.timestamp).toLocaleTimeString();
        return `${req.method} ${req.status ?? '---'} ${req.url} (${time})`;
      }).join('\n') + '\n';
    }

    case 'conversation': {
      const messages = data.conversationHistory as Array<{ role: string; content: string; timestamp: string }> | undefined;
      if (!messages?.length) return header + 'No conversation messages\n';
      return header + messages.map(msg => {
        const time = new Date(msg.timestamp).toLocaleTimeString();
        const role = msg.role.charAt(0).toUpperCase() + msg.role.slice(1);
        return `[${time}] ${role}: ${msg.content}`;
      }).join('\n') + '\n';
    }

    case 'events': {
      const { events, errors } = data as unknown as JobArtifactResponses['events'];
      // Interleave events and page errors into one time-ordered timeline so the
      // agent sees failures in the context of the actions that surrounded them.
      const lines = [
        ...events.map(event => ({
          ts: event.timestamp,
          text: `[${new Date(event.timestamp).toLocaleTimeString()}] [${event.type}] ${describeEvent(event)}`,
        })),
        ...errors.map(error => ({
          ts: error.timestamp,
          text: `[${new Date(error.timestamp).toLocaleTimeString()}] [error] ${error.message}`,
        })),
      ].sort((a, b) => Date.parse(a.ts) - Date.parse(b.ts));
      if (lines.length === 0) return header + 'No events or errors recorded\n';
      return header + lines.map(l => l.text).join('\n') + '\n';
    }

    case 'page-states': {
      const { pageStates } = data as unknown as JobArtifactResponses['page-states'];
      if (!pageStates.length) return header + 'No page states recorded\n';
      return header + pageStates.map(state => {
        const time = new Date(state.timestamp).toLocaleTimeString();
        return `[${time}] ${describePageState(state)}`;
      }).join('\n') + '\n';
    }

    case 'key-moments': {
      const moments = data.keyMoments as Array<{ type: string; description: string; significance: number; timestamp: string }> | undefined;
      if (!moments?.length) return header + 'No key moments available\n';
      return header + moments.map(m => {
        const time = new Date(m.timestamp).toLocaleTimeString();
        return `[${m.type}] ${time} ${m.description} (significance: ${m.significance})`;
      }).join('\n') + '\n';
    }

    case 'recording': {
      const status = data.status as string;
      if (status !== 'ready') return header + `Recording not available (status: ${status})\n`;
      const recordings = data.recordings as Array<{ sessionIndex: number; url: string; audioStripped: boolean }>;
      return header + recordings.map(r =>
        `Session ${r.sessionIndex}: ${r.url}${r.audioStripped ? ' (audio removed)' : ''}`,
      ).join('\n') + '\n';
    }
  }
}

function formatSeconds(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export async function handleGetTestArtifacts(
  args: GetTestArtifactsArgs,
  jobClient: JobClient,
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  const { jobId } = args;
  const requestedTypes: JobArtifactType[] = args.artifacts?.length
    ? args.artifacts as JobArtifactType[]
    : [...JOB_ARTIFACT_TYPES];

  const sections: string[] = [];
  const errors: string[] = [];

  for (const artifactType of requestedTypes) {
    try {
      const data = await jobClient.getJobArtifact(jobId, artifactType);
      sections.push(formatArtifact(artifactType, data));
    } catch (error) {
      const err = error as Error & { status?: number };
      if (err.status === 403) {
        errors.push(`${artifactType}: Requires paid plan`);
      } else {
        errors.push(`${artifactType}: ${err.message}`);
      }
    }
  }

  if (sections.length === 0 && errors.length > 0) {
    return {
      content: [{
        type: 'text',
        text: `Failed to retrieve artifacts for job ${jobId}\n\n${errors.join('\n')}\n\nUpgrade at https://runhuman.com/pricing`,
      }],
      isError: true,
    };
  }

  let text = `Test Artifacts for Job: ${jobId}\n\n${sections.join('\n')}`;
  if (errors.length > 0) {
    text += `\nSome artifacts unavailable:\n${errors.join('\n')}\n`;
  }

  return {
    content: [{ type: 'text', text }],
  };
}
